import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box } from '@mui/material';
import React from 'react';
import { ClickableCard } from './ClickableCard';
import { DraggableCard } from './DraggableCard';
import { StandardCard } from './StandardCard';
import { getCardColors } from './cardColors';
import { CardActions, CardIcon } from './components';
const dragHandleWidth = 22;
/**
 * IconCard component displays a card with a fixed icon and optional draggable or clickable behaviors.
 *
 * @param {IconCardProps} props - The properties for the IconCard.
 * @param {boolean} [props.moving] - Flag indicating if the card is in a moving (dragged) state.
 * @param {boolean} [props.dragHandle] - Flag indicating if a drag handle should be displayed.
 * @param {React.ReactNode} [props.children] - Content to render within the card.
 * @param {() => void} [props.onCardClick] - Handler for card click action.
 * @param {(point: Point) => boolean} [props.onPointerDragStart] - Handler for starting a pointer drag.
 * @param {(point: Point) => void} [props.onPointerDragEnd] - Handler for ending a pointer drag.
 * @param {() => void} [props.onPointerDragCancel] - Handler for canceling a pointer drag.
 * @param {() => void} [props.onSetMoving] - Optional handler to signal a card is being moved.
 * @param {CardAction[]} [props.actions] - Optional card actions to display.
 * @param {string} props.color - The background color key for the icon section.
 * @returns {JSX.Element} A styled card component with icon and optional interactivity.
 */
export const IconCard = (props) => {
    const Card = props.onSetMoving || props.onPointerDragStart ? DraggableCard : props.onCardClick ? ClickableCard : StandardCard;
    const ref = React.useRef(null);
    const [pointerDragging, setPointerDragging] = React.useState(false);
    const cardIconAndActions = (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: {
                position: 'absolute',
                top: 'calc(50% - 40px)',
                left: 1 + (props.dragHandle ? dragHandleWidth : 0),
                pointerEvents: 'none',
            } },
            React.createElement(CardIcon, Object.assign({}, props))),
        React.createElement(Box, { sx: { position: 'absolute', top: 'calc(50% - 20px)', right: 1 } },
            React.createElement(CardActions, Object.assign({}, props)))));
    return (React.createElement(Box, { ref: ref, sx: { position: 'relative', transform: props.moving ? 'rotate(2deg)' : 'none' } },
        React.createElement(Card, Object.assign({}, props, { onPointerDragStart: (point) => {
                const pointerDragStarted = props.onPointerDragStart ? props.onPointerDragStart(point) : false;
                if (pointerDragStarted) {
                    setPointerDragging(true);
                }
                return pointerDragStarted;
            }, onPointerDragEnd: (point) => {
                var _a;
                setPointerDragging(false);
                (_a = props.onPointerDragEnd) === null || _a === void 0 ? void 0 : _a.call(props, point);
            }, onPointerDragCancel: () => {
                var _a;
                setPointerDragging(false);
                (_a = props.onPointerDragCancel) === null || _a === void 0 ? void 0 : _a.call(props);
            }, renderDragCard: (dragCard, dragCardSx) => (React.createElement(Box, { sx: [{ position: 'relative' }, ...(Array.isArray(dragCardSx) ? dragCardSx : [dragCardSx])] },
                dragCard,
                cardIconAndActions)), getCardBoundingRect: () => {
                const element = ref.current;
                if (element) {
                    element.style.transform = 'none';
                    const rect = element.getBoundingClientRect();
                    element.style.transform = '';
                    return rect;
                }
                else {
                    return undefined;
                }
            }, sx: {
                display: 'grid',
                gridTemplateColumns: (props.dragHandle ? `${dragHandleWidth}px ` : '') + 'auto 1fr auto',
                transform: 'none',
            } }),
            props.dragHandle && (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                React.createElement(DragIndicatorIcon, { fontSize: 'small' }))),
            React.createElement(Box, { sx: {
                    width: 80,
                    minHeight: 80,
                    backgroundColor: getCardColors()[props.color],
                    borderTopLeftRadius: 4,
                    borderBottomLeftRadius: 4,
                    pointerEvents: 'none',
                } }),
            React.createElement(Box, { sx: { alignSelf: 'center', overflow: 'hidden' } }, props.children),
            props.actions && props.actions.length > 0 && (React.createElement(Box, { sx: {
                    width: 40 * props.actions.length + 8 * props.actions.length,
                    height: 40,
                } }))),
        !pointerDragging && cardIconAndActions));
};
